/**
 * Lets a customer apply an additional discount code to their cart directly from the cart drawer,
 * without a full page reload. The classic Ajax Cart API has no endpoint for discount codes, so this
 * talks to Shopify's Storefront API instead (cartDiscountCodesUpdate), using the same cart the
 * classic /cart/add.js and /cart/change.js calls already created -- the cart's token doubles as the
 * Storefront API Cart ID (gid://shopify/Cart/{token}), so no separate cart is created.
 *
 * After a successful apply, it re-fetches the same sections the rest of the drawer already uses
 * (see assets/cart-drawer.js) so the discount banner, totals, and line items all reflect the change.
 */
(function () {
  function getCartToken() {
    const match = document.cookie.match(/(?:^|; )cart=([^;]+)/);
    if (!match) return null;
    return decodeURIComponent(match[1]);
  }

  function storefrontFetch(query, variables) {
    const config = window.storefrontApiConfig;
    if (!config || !config.domain || !config.token) {
      return Promise.reject(new Error('Storefront API is not configured'));
    }
    return fetch(`https://${config.domain}/api/${config.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.token,
      },
      body: JSON.stringify({ query, variables }),
    }).then((response) => response.json());
  }

  const CART_DISCOUNTS_QUERY = `
    query cartDiscountCodes($cartId: ID!) {
      cart(id: $cartId) {
        discountCodes { code applicable }
      }
    }
  `;

  const CART_DISCOUNT_CODES_UPDATE_MUTATION = `
    mutation cartDiscountCodesUpdate($cartId: ID!, $codes: [String!]) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $codes) {
        cart { id }
        userErrors { field message }
      }
    }
  `;

  function getApplicableCodes(cartId) {
    return storefrontFetch(CART_DISCOUNTS_QUERY, { cartId }).then((result) => {
      const codes = (result.data && result.data.cart && result.data.cart.discountCodes) || [];
      return codes;
    });
  }

  function setDiscountCodes(cartId, codes) {
    return storefrontFetch(CART_DISCOUNT_CODES_UPDATE_MUTATION, { cartId, codes }).then((result) => {
      const payload = result.data && result.data.cartDiscountCodesUpdate;
      const userErrors = (payload && payload.userErrors) || [];
      if (!payload || userErrors.length > 0) {
        throw new Error((userErrors[0] && userErrors[0].message) || 'discount_update_failed');
      }
      return payload.cart;
    });
  }

  async function applyDiscountCode(newCode) {
    const cartToken = getCartToken();
    if (!cartToken) throw new Error('no_active_cart');

    const cartId = 'gid://shopify/Cart/' + cartToken;
    const before = await getApplicableCodes(cartId);
    const existingCodes = before.filter((code) => code.applicable).map((code) => code.code);

    await setDiscountCodes(cartId, existingCodes.concat([newCode]));

    const after = await getApplicableCodes(cartId);
    const matched = after.find((code) => code.code.toLowerCase() === newCode.toLowerCase());

    if (!matched || !matched.applicable) {
      // Roll back to the previously-applicable set so a bad code doesn't linger on the cart.
      await setDiscountCodes(cartId, existingCodes);
      throw new Error('not_applicable');
    }
  }

  function refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return Promise.resolve();

    // Re-fetch the same sections the drawer already uses for add-to-cart/qty updates (see
    // assets/cart-drawer.js) and swap them in directly -- deliberately not going through the
    // global cartUpdate pub/sub here, since that also notifies the standalone cart page's
    // <cart-items>, which isn't the element we just changed.
    const sectionIds = cartDrawer.getSectionsToRender().map((section) => section.id);
    return fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`)
      .then((response) => response.json())
      .then((sections) => {
        cartDrawer.renderContents({ sections });
        initAllBanners();
      });
  }

  function initBanner(banner) {
    const prompt = banner.querySelector('[data-add-code-prompt]');
    const toggle = banner.querySelector('[data-add-code-toggle]');
    const form = banner.querySelector('[data-add-code-form]');
    const input = banner.querySelector('[data-add-code-input]');
    const submit = banner.querySelector('[data-add-code-submit]');
    const error = banner.querySelector('[data-add-code-error]');
    if (!toggle || !form || !input || !submit) return;

    toggle.addEventListener('click', () => {
      if (prompt) prompt.hidden = true;
      form.classList.add('is-expanded');
      toggle.setAttribute('aria-expanded', 'true');
      input.focus();
    });

    function showError(message) {
      if (!error) return;
      error.textContent = message;
      error.classList.add('is-visible');
    }

    function clearError() {
      if (!error) return;
      error.classList.remove('is-visible');
      error.textContent = '';
    }

    function submitCode() {
      const code = input.value.trim();
      if (!code) return;

      clearError();
      submit.setAttribute('aria-busy', 'true');

      applyDiscountCode(code)
        .then(() => refreshCartDrawer())
        .catch((err) => {
          const message =
            (window.cartDiscountBannerStrings && window.cartDiscountBannerStrings.invalidError) ||
            "That code isn't valid, or can't be combined with your current discount.";
          showError(message);
          console.error('[cart-discount-code]', err);
        })
        .finally(() => {
          submit.removeAttribute('aria-busy');
        });
    }

    submit.addEventListener('click', submitCode);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        submitCode();
      }
    });
  }

  function initAllBanners() {
    document.querySelectorAll('[data-cart-add-code-widget]').forEach(initBanner);
  }

  function resetWidget(widget) {
    const prompt = widget.querySelector('[data-add-code-prompt]');
    const toggle = widget.querySelector('[data-add-code-toggle]');
    const form = widget.querySelector('[data-add-code-form]');
    const input = widget.querySelector('[data-add-code-input]');
    const error = widget.querySelector('[data-add-code-error]');

    if (prompt) prompt.hidden = false;
    if (form) form.classList.remove('is-expanded');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (input) input.value = '';
    if (error) {
      error.classList.remove('is-visible');
      error.textContent = '';
    }
  }

  function resetAllWidgets() {
    document.querySelectorAll('[data-cart-add-code-widget]').forEach(resetWidget);
  }

  function watchDrawerOpenClose() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Collapse the input back to the prompt and clear any error every time the drawer opens or
    // closes -- the drawer's open()/close() (see assets/cart-drawer.js) only toggle its class
    // attribute, so watching that covers both without needing to touch that file.
    new MutationObserver(resetAllWidgets).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllBanners();
      watchDrawerOpenClose();
    });
  } else {
    initAllBanners();
    watchDrawerOpenClose();
  }

  // The drawer's contents (including the banner) get swapped wholesale on every cart update,
  // so newly-inserted "Add code" controls need their listeners re-bound each time.
  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, initAllBanners);
  }
})();
